import { CHAMP_PIECES_JOINTES, champsDeCondition, phraseDeCondition } from '@taki/data-model';
import type { Condition, LibellesDeCondition } from '@taki/data-model';
import type { ChampDeclare, ViewBinding } from '@taki/ui';
import { raisonDeScriptAbsent } from '@taki/ui';
import type { ActionDeFicheAssemblee } from './assemblage.js';
import type { FicheGenerique } from './app-declaree.js';
import { RAISON_AUCUN_FLUX, RAISON_VERIFICATION } from './boutons-flux.js';
import type { EtatDuBranchement } from './controleur-boutons-flux.js';

/**
 * L'ETAT D'UN SMART BOUTON DE FICHE : visible ou retire, vivant ou grise, et la
 * raison du grise (G16 : un controle eteint dit POURQUOI, et il ne disparait
 * jamais, sauf quand la declaration le demande : `siFaux: "masquer"`).
 *
 * UNE FONCTION PURE, parce que c'est la seule chose que la personne lit : la
 * priorite des raisons s'y tient, un test la rejoue sans navigateur.
 *
 * LA PRIORITE DES RAISONS, du plus general au plus particulier : l'ACCES (lecture
 * seule), puis un SCRIPT pas encore code, puis AUCUN FLUX branche, puis la
 * CONDITION de la declaration. Le serveur rejoue la condition sur la fiche
 * enregistree : celle-ci n'est que la lecture immediate de l'ecran.
 */

export interface EntreeDeLEtat {
  action: ActionDeFicheAssemblee;
  /** La porte ne s'ecrit pas, et pourquoi (`useScreen().raisonAcces`). */
  raisonAcces: string | null;
  /** Le nom du script que l'app ne fournit pas (instance d'org), ou `null`. */
  scriptAbsent: string | null;
  /** Le branchement d'une action flux, `undefined` pour une autre. */
  branchement?: EtatDuBranchement | undefined;
  /** La fiche ouverte, BRUTE (telle qu'enregistree). */
  fiche: FicheGenerique | undefined;
  /** `ecran.evaluer(porteId, contexte)` : la lecture des verrous d'en-tete, avec le fuseau de l'org et moi. */
  evaluer?: ((condition: Condition, fiche: FicheGenerique, surcharge?: (nom: string) => unknown) => boolean) | undefined;
  libelles: LibellesDeCondition;
  /** La liste des pieces jointes pretes, `'chargement'`, `'erreur'`, ou `undefined` tant qu'aucune condition ne la cite. */
  piecesJointes: readonly unknown[] | 'chargement' | 'erreur' | undefined;
}

export interface EtatDuBouton {
  /** Retire de la rangee (`siFaux: "masquer"`, condition fausse). */
  masque: boolean;
  /** La raison du grise, ou `null` : le bouton est vivant. */
  raison: string | null;
}

/** Cette condition cite-t-elle la liste des pieces jointes ? C'est elle qui declenche la lecture. */
export function citePiecesJointes(condition: Condition | undefined): boolean {
  return condition !== undefined && champsDeCondition(condition).includes(CHAMP_PIECES_JOINTES);
}

export function etatDuBouton(entree: EntreeDeLEtat): EtatDuBouton {
  const { action, fiche, evaluer, piecesJointes } = entree;
  const condition = action.si;

  /* LA CONDITION, JUGEE QUAND ON LE PEUT : la fiche est lue, et la liste des pieces jointes aussi si elle la cite. */
  let verdict: boolean | 'attente' | 'erreur' | null = null;
  if (condition !== undefined) {
    const cite = citePiecesJointes(condition);
    if (cite && piecesJointes === 'erreur') verdict = 'erreur';
    else if (fiche === undefined || evaluer === undefined || (cite && !Array.isArray(piecesJointes))) verdict = 'attente';
    else {
      try {
        verdict = evaluer(condition, fiche, (nom) => (nom === CHAMP_PIECES_JOINTES ? piecesJointes : undefined));
      } catch {
        /* UNE CONDITION ILLISIBLE NE FAIT PAS TOMBER LA FICHE : elle est fausse. */
        verdict = false;
      }
    }
  }
  if (verdict === false && action.siFaux === 'masquer') return { masque: true, raison: null };

  if (entree.raisonAcces !== null) return { masque: false, raison: entree.raisonAcces };
  if (entree.scriptAbsent !== null) return { masque: false, raison: raisonDeScriptAbsent(entree.scriptAbsent) };
  if (entree.branchement === 'aucun') return { masque: false, raison: RAISON_AUCUN_FLUX };
  if (entree.branchement === 'chargement') return { masque: false, raison: RAISON_VERIFICATION };
  if (verdict === 'attente') return { masque: false, raison: RAISON_VERIFICATION };
  if (verdict === 'erreur') return { masque: false, raison: 'Pièces jointes illisibles' };
  if (verdict === false && condition !== undefined) {
    return { masque: false, raison: action.raison ?? phraseDeCondition(condition, entree.libelles) };
  }
  return { masque: false, raison: null };
}

/** Le nom lisible de `socle:piecesJointes`. */
export const LIBELLE_PIECES_JOINTES = 'Pièces jointes';

/**
 * LES MOTS QUE LA PHRASE D'UNE CONDITION EMPRUNTE a l'ecran : le libelle d'un
 * champ et celui d'une de ses valeurs, comme la fiche les montre (champs
 * declares du jeu, colonnes du binding de la fiche, role de statut). Un nom
 * inconnu se dit tel quel, jamais vide.
 */
export function libellesDeCondition(
  champs: readonly ChampDeclare[],
  binding: ViewBinding<FicheGenerique> | undefined,
): LibellesDeCondition {
  const nu = (nom: string): string => (nom.startsWith('socle:') ? nom.slice('socle:'.length) : nom);
  const colonnes = [...(binding?.fieldColumns ?? []), ...(binding?.columns ?? [])];
  const options = (nom: string): readonly { value: string; label: string }[] => {
    const cle = nu(nom);
    const colonne = colonnes.find((candidate) => candidate.key === cle);
    const deColonne = colonne?.options?.();
    if (deColonne !== undefined && deColonne.length > 0) return deColonne;
    const declare = champs.find((champ) => champ.key === cle);
    if (declare?.options !== undefined) return declare.options;
    const statut = binding?.roles['status'];
    if (cle === 'status' && statut?.options !== undefined) return statut.options();
    return [];
  };
  return {
    champ: (nom) => {
      if (nom === CHAMP_PIECES_JOINTES) return LIBELLE_PIECES_JOINTES;
      const cle = nu(nom);
      const colonne = colonnes.find((candidate) => candidate.key === cle);
      if (colonne !== undefined) return colonne.label;
      const declare = champs.find((champ) => champ.key === cle);
      if (declare !== undefined) return declare.label;
      if (cle === 'status') return binding?.roles['status']?.label ?? 'Statut';
      return cle;
    },
    valeur: (nom, valeur) => {
      if (typeof valeur === 'boolean') return valeur ? 'Oui' : 'Non';
      const trouvee = options(nom).find((option) => option.value === String(valeur));
      return trouvee?.label ?? String(valeur);
    },
  };
}
